import { DateTime } from 'luxon'
import { getPresetForRange } from '~/components/routes/network-dashboard/network-filters/date-presets'
import {
  NETWORK_DASHBOARD_ALL_TIME_START_ISO,
  type NetworkDashboardDateRange,
} from '~/components/routes/network-dashboard/network-filters/types'
import type { DateRangePreset } from '~/components/ui/date-range-picker'

// Presets réutilisés du Network Dashboard (jeu complet — le picker RH n'affiche que
// NETWORK_DASHBOARD_DATE_PRESETS, mais l'URL peut porter n'importe quel slug standard).
export const HR_DATE_PRESETS = [
  'yesterday',
  'today',
  '7d',
  '30d',
  '3m',
  '12m',
  'mtd',
  'ytd',
  'all',
] as const satisfies readonly DateRangePreset[]

export interface HrDashboardRangeSearch {
  startDate?: string
  endDate?: string
  datePreset?: DateRangePreset
}

export interface ResolvedHrDashboardRange {
  /** ISO 'YYYY-MM-DD' envoyé aux queries (`dateStart`). « Toujours » ⇒ NETWORK_DASHBOARD_ALL_TIME_START_ISO. */
  startIso: string
  /** ISO 'YYYY-MM-DD' envoyé aux queries (`dateEnd`). */
  endIso: string
  /** Forme attendue par `DateRangePicker` / `NetworkFilters` (`from: null` = « Toujours »). */
  range: NetworkDashboardDateRange
  /** Préset actif pour la surbrillance du picker, ou null pour une plage custom. */
  preset: DateRangePreset | null
}

/**
 * Plage par défaut du dashboard RH = SEMAINE PRÉCÉDENTE (lundi → dimanche), et non plus « 7 jours
 * glissants ». Luxon `startOf('week')` = lundi (ISO 8601). Spécifique au RH : le Network Dashboard
 * garde son défaut 7j (`getDefaultDateRange`).
 */
export function getHrDefaultRange(): { from: Date; to: Date } {
  const start = DateTime.now().startOf('week').minus({ weeks: 1 })
  const end = start.plus({ days: 6 })
  return { from: start.toJSDate(), to: end.toJSDate() }
}

/**
 * Libellé du badge de temporalité RH : « Semaine N » quand la plage est une semaine ISO complète
 * (lundi → dimanche), sinon `undefined` pour laisser `SelectionPeriodBadge` afficher « X jours
 * glissants ». Couvre le défaut (semaine précédente) et toute sélection d'une semaine pleine.
 */
export function hrDashboardPeriodLabel(startIso: string, endIso: string): string | undefined {
  const start = DateTime.fromISO(startIso)
  const end = DateTime.fromISO(endIso)
  if (start.weekday === 1 && end.weekday === 7 && end.diff(start, 'days').days === 6) {
    return `Semaine ${start.weekNumber}`
  }
  return undefined
}

/**
 * Résout les params d'URL du dashboard RH (`startDate`/`endDate`/`datePreset`) en un range
 * concret. Défaut = semaine précédente (`getHrDefaultRange`).
 */
export function resolveHrDashboardRange(search: HrDashboardRangeSearch): ResolvedHrDashboardRange {
  const defaultRange = getHrDefaultRange()
  const startIso = search.startDate ?? DateTime.fromJSDate(defaultRange.from).toISODate()!
  const endIso = search.endDate ?? DateTime.fromJSDate(defaultRange.to).toISODate()!

  const range: NetworkDashboardDateRange = {
    from:
      startIso === NETWORK_DASHBOARD_ALL_TIME_START_ISO
        ? null
        : DateTime.fromISO(startIso).toJSDate(),
    to: DateTime.fromISO(endIso).toJSDate(),
  }

  return { startIso, endIso, range, preset: search.datePreset ?? getPresetForRange(range) }
}

/**
 * Sérialise le range renvoyé par `NetworkFilters` vers les params d'URL du dashboard RH.
 * Miroir de `buildSingleSelectionSearch` (Réseau) restreint aux 3 clés RH.
 */
export function serializeHrDashboardRange(
  dateRange: NetworkDashboardDateRange,
  datePreset: DateRangePreset | null | undefined,
): { startDate: string; endDate: string; datePreset: DateRangePreset | undefined } {
  return {
    startDate: dateRange.from
      ? DateTime.fromJSDate(dateRange.from).toISODate()!
      : NETWORK_DASHBOARD_ALL_TIME_START_ISO,
    endDate: DateTime.fromJSDate(dateRange.to).toISODate()!,
    datePreset: datePreset ?? undefined,
  }
}
